import { useEffect, useState } from "react";
import { styles } from "../../styles";
import { Field, ErrorNote } from "../common";
import { useShop } from "../../context/ShopContext";
import { useToast } from "../../context/ToastContext";
import { saveShopConfig } from "../../data/config";
import { logAudit } from "../../data/audit";

// A compact, common set of US zones plus UTC. The field is a free-form fallback
// so any IANA zone can be entered if needed.
const COMMON_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "UTC",
];

export function SettingsCard() {
  const { config } = useShop();
  const notify = useToast();
  const [email, setEmail] = useState(config.shopEmail);
  const [staff, setStaff] = useState(config.staffEmails.join("\n"));
  const [timezone, setTimezone] = useState(config.timezone);
  const [blockSameDay, setBlockSameDay] = useState(config.blockSameDay);
  const [cooldown, setCooldown] = useState(String(config.cooldownMinutes));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => setEmail(config.shopEmail), [config.shopEmail]);
  useEffect(() => setStaff(config.staffEmails.join("\n")), [config.staffEmails]);
  useEffect(() => setTimezone(config.timezone), [config.timezone]);
  useEffect(() => setBlockSameDay(config.blockSameDay), [config.blockSameDay]);
  useEffect(() => setCooldown(String(config.cooldownMinutes)), [config.cooldownMinutes]);

  // Split on commas or newlines; trim and drop blanks.
  const staffList = staff.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
  const cooldownNum = Math.max(0, Math.round(Number(cooldown) || 0));
  const dirty =
    email !== config.shopEmail ||
    JSON.stringify(staffList) !== JSON.stringify(config.staffEmails) ||
    timezone !== config.timezone ||
    blockSameDay !== config.blockSameDay ||
    cooldownNum !== config.cooldownMinutes;

  const save = async () => {
    setError(null);
    const re = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    if (!re.test(email.trim())) {
      setError("Enter a valid shop email so invites can reach you.");
      return;
    }
    const badStaff = staffList.find((s) => !re.test(s));
    if (badStaff) {
      setError(`"${badStaff}" is not a valid email address.`);
      return;
    }
    const clash = staffList.find((s) => s.toLowerCase() === email.trim().toLowerCase());
    if (clash) {
      setError("A staff calendar email must be different from the shop email, or that calendar will not receive appointments.");
      return;
    }
    setBusy(true);
    try {
      await saveShopConfig({
        shopEmail: email.trim(), staffEmails: staffList, timezone: timezone.trim(),
        blockSameDay, cooldownMinutes: cooldownNum,
      });
      void logAudit("Updated shop settings");
      notify("Settings saved.");
    } catch {
      setError("We could not save your settings. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.card}>
      <h2 style={styles.cardTitle}>Shop settings</h2>
      <p style={styles.sub}>Where booking copies and calendar invites go, and the timezone used on invites.</p>
      {error && <ErrorNote>{error}</ErrorNote>}

      <Field
        label="Shop email (organizer)" value={email} onChange={setEmail}
        type="email" autoComplete="email" placeholder="hello@yourshop.com"
        hint="Business inbox that gets a copy of every booking and organizes each invite."
      />

      <div style={styles.field}>
        <label htmlFor="staff" style={styles.label}>Staff calendar emails</label>
        <textarea
          id="staff" value={staff} onChange={(e) => setStaff(e.target.value)}
          className="input" style={{ ...styles.textarea, minHeight: 64 }} rows={2}
          placeholder="groomer@gmail.com" aria-label="Staff calendar emails"
        />
        <span style={styles.hint}>
          One per line. Each appointment is added to these calendars. They must be different from the shop email above.
        </span>
      </div>

      <div style={styles.field}>
        <label htmlFor="tz" style={styles.label}>Timezone</label>
        <select
          id="tz" value={COMMON_TIMEZONES.includes(timezone) ? timezone : "__custom"}
          onChange={(e) => { if (e.target.value !== "__custom") setTimezone(e.target.value); }}
          className="input select" style={styles.breedSelect}
        >
          {COMMON_TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          {!COMMON_TIMEZONES.includes(timezone) && <option value="__custom">{timezone} (custom)</option>}
        </select>
      </div>

      <div style={{ ...styles.hourRow, marginTop: 4 }}>
        <label style={styles.toggleWrap}>
          <input
            type="checkbox" checked={blockSameDay}
            onChange={(e) => setBlockSameDay(e.target.checked)}
            className="sr-check" aria-label="Block same-day bookings"
          />
          <span className="switch" style={{ ...styles.switch, ...(blockSameDay ? styles.switchOn : {}) }}>
            <span style={{ ...styles.knob, ...(blockSameDay ? styles.knobOn : {}) }} />
          </span>
          <span style={styles.dayName}>No same-day bookings</span>
        </label>
      </div>
      <p style={styles.hint}>When on, today is closed to new bookings, so the soonest a customer can book is tomorrow.</p>

      <div style={{ ...styles.field, marginTop: 14 }}>
        <label htmlFor="cooldown" style={styles.label}>Buffer between appointments (minutes)</label>
        <input
          id="cooldown" value={cooldown} onChange={(e) => setCooldown(e.target.value)}
          inputMode="numeric" className="input" style={{ ...styles.input, maxWidth: 120 }}
          aria-label="Buffer minutes between appointments"
        />
        <span style={styles.hint}>Minimum cleanup or break time the groomer needs between dogs. Set 0 for back-to-back.</span>
      </div>

      <div style={styles.msgActions}>
        <button className="primary" style={styles.primarySm} onClick={save} disabled={busy || !dirty}>
          {busy ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
}
